import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useMMAuth } from '@machinemetrics/mm-react-tools';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { calculateCapacity } from '../../utils/calculateCapacity';
import { DEFAULT_COMPANY } from '../../constants/company';

const SIM_API  = '/machinesim/api/sim';
const BASE_API = '/machinesim/api';

function machineDisplayName(machineId, companySlug) {
  if (companySlug && companySlug !== 'default' && machineId.startsWith(`${companySlug}-`)) {
    return machineId.slice(companySlug.length + 1);
  }
  return machineId;
}

const card = {
  background: 'var(--cn-surface)',
  border: '1px solid rgba(168,144,96,0.12)',
  borderRadius: 8,
  padding: '24px 28px',
  marginBottom: 20,
};

const sectionTitle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--cn-gold)',
  marginBottom: 16,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--cn-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 6,
  display: 'block',
};

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(168,144,96,0.2)',
  borderRadius: 4,
  color: 'var(--cn-text)',
  fontSize: 14,
  padding: '8px 12px',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: '"Courier New", monospace',
};

function btnStyle(variant) {
  const base = {
    padding: '9px 18px', borderRadius: 4, fontSize: 12, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', border: 'none',
  };
  if (variant === 'primary') return { ...base, background: 'var(--cn-gold)', color: '#0D1B2A' };
  if (variant === 'danger')  return { ...base, background: 'rgba(192,57,43,0.15)', color: 'var(--cn-danger)', border: '1px solid rgba(192,57,43,0.3)' };
  return { ...base, background: 'rgba(168,144,96,0.1)', color: 'var(--cn-gold)', border: '1px solid rgba(168,144,96,0.25)' };
}

const TH = ({ children }) => (
  <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, color: 'var(--cn-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid rgba(168,144,96,0.1)' }}>
    {children}
  </th>
);

function StatusBadge({ phase }) {
  const map = {
    cycle:       { bg: 'rgba(46,125,94,0.15)',   text: 'var(--cn-success)' },
    setup:       { bg: 'rgba(183,121,31,0.15)',  text: 'var(--cn-warning)' },
    idle:        { bg: 'rgba(255,255,255,0.06)', text: 'var(--cn-muted)'   },
    starved:     { bg: 'rgba(192,57,43,0.12)',   text: 'var(--cn-danger)'  },
    faulted:     { bg: 'rgba(192,57,43,0.2)',    text: 'var(--cn-danger)'  },
    tool_change: { bg: 'rgba(37,99,235,0.15)',   text: 'var(--cn-info)'    },
  };
  const c = map[phase] || map.idle;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', background: c.bg, color: c.text }}>
      {phase || 'idle'}
    </span>
  );
}

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.2)', borderRadius: 8, padding: '28px 32px', maxWidth: 400, width: '90%' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--cn-text)', marginBottom: 20 }}>{message}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button style={btnStyle('ghost')} onClick={onCancel}>Cancel</button>
          <button style={btnStyle('danger')} onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ─── Section 1: Simulation Speed ─────────────────────────────────
function SpeedSection({ engineStatus, companySlug }) {
  const [multiplier, setMultiplier] = useState(1);
  const [saving, setSaving]         = useState(false);
  const [feedback, setFeedback]     = useState('');
  const [selected, setSelected]     = useState(new Set());
  const seenRef                     = useRef(new Set());

  const machines = Object.values(engineStatus);

  // Auto-select any machine we haven't seen before (covers first load + runtime adds)
  useEffect(() => {
    const newIds = machines.map(m => m.machineId).filter(id => !seenRef.current.has(id));
    if (newIds.length === 0) return;
    newIds.forEach(id => seenRef.current.add(id));
    setSelected(prev => new Set([...prev, ...newIds]));
  }, [machines.map(m => m.machineId).sort().join(',')]);

  const allSelected = machines.length > 0 && machines.every(m => selected.has(m.machineId));
  const noneSelected = selected.size === 0;

  const toggleMachine = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(machines.map(m => m.machineId)));

  const apply = async () => {
    if (noneSelected) return;
    setSaving(true);
    setFeedback('');
    const cq = companySlug && companySlug !== 'default' ? `?company=${companySlug}` : '';
    try {
      if (selected.size === machines.length) {
        await axios.post(`${SIM_API}/speed-all${cq}`, { multiplier });
      } else {
        await Promise.all([...selected].map(id =>
          axios.post(`${SIM_API}/${id}/setSpeed${cq}`, { multiplier })
        ));
      }
      const label = selected.size === machines.length
        ? 'all machines'
        : `${selected.size} machine${selected.size !== 1 ? 's' : ''}`;
      setFeedback(`Applied ${multiplier}x to ${label}`);
      setTimeout(() => setFeedback(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const btnLabel = noneSelected
    ? 'Apply (none selected)'
    : allSelected
      ? 'Apply to All'
      : `Apply to ${selected.size}`;

  return (
    <div style={card}>
      <div style={sectionTitle}>Simulation Speed</div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 20 }}>
        <div style={{ flex: 1, maxWidth: 200 }}>
          <span style={labelStyle}>Multiplier</span>
          <input
            type="number" min={1} max={1000} step={1} value={multiplier}
            onChange={e => setMultiplier(Number(e.target.value))}
            style={inputStyle}
          />
        </div>
        <input
          type="range" min={1} max={100} step={1} value={Math.min(multiplier, 100)}
          onChange={e => setMultiplier(Number(e.target.value))}
          style={{ width: 160, accentColor: 'var(--cn-gold)', cursor: 'pointer', marginBottom: 2 }}
        />
        <button style={btnStyle('primary')} onClick={apply} disabled={saving || noneSelected}>
          {saving ? 'Applying…' : btnLabel}
        </button>
      </div>
      {feedback && <div style={{ fontSize: 12, color: 'var(--cn-success)', marginBottom: 12 }}>{feedback}</div>}
      {machines.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ padding: '6px 10px', width: 32 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  style={{ accentColor: 'var(--cn-gold)', cursor: 'pointer' }}
                />
              </th>
              <TH>Machine</TH><TH>Phase</TH><TH>Speed</TH>
            </tr>
          </thead>
          <tbody>
            {machines.map(m => (
              <tr key={m.machineId}>
                <td style={{ padding: '7px 10px' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(m.machineId)}
                    onChange={() => toggleMachine(m.machineId)}
                    style={{ accentColor: 'var(--cn-gold)', cursor: 'pointer' }}
                  />
                </td>
                <td style={{ padding: '7px 10px', fontFamily: '"Courier New", monospace', color: 'var(--cn-text)' }}>{machineDisplayName(m.machineId, companySlug)}</td>
                <td style={{ padding: '7px 10px' }}><StatusBadge phase={m.phase} /></td>
                <td style={{ padding: '7px 10px', fontFamily: '"Courier New", monospace', color: 'var(--cn-gold)' }}>{m.speedMultiplier}x</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Section 2: Demand Tuning ─────────────────────────────────────
function DemandSection({ companySlug }) {
  const [config, setConfig]         = useState({ wosMin: 24, wosMax: 40, qtyMin: 35, qtyMax: 65, horizonDays: 5 });
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [feedback, setFeedback]     = useState('');
  const [confirm, setConfirm]       = useState(null);
  const [preview, setPreview]       = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const cq = companySlug && companySlug !== 'default' ? `?company=${companySlug}` : '';

  useEffect(() => {
    setLoading(true);
    axios.get(`${SIM_API}/demand${cq}`)
      .then(r => setConfig(r.data.demand))
      .finally(() => setLoading(false));
  }, [companySlug]);

  const num = (field, val) => setConfig(c => ({ ...c, [field]: Number(val) }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${SIM_API}/demand${cq}`, config);
      setConfig(r.data.demand);
      setFeedback('Saved');
      setTimeout(() => setFeedback(''), 2500);
    } finally { setSaving(false); }
  };

  const apply = async (mode) => {
    setConfirm(null);
    setSaving(true);
    setFeedback('');
    try {
      await axios.post(`${SIM_API}/demand${cq}`, config);
      const r = await axios.post(`${SIM_API}/demand/apply${cq}`, { mode });
      setFeedback(
        mode === 'recalculate'
          ? `Recalculated — deleted ${r.data.deletedWoCount} open WOs, new batch generated`
          : 'Will apply on next auto-loop cycle'
      );
      setTimeout(() => setFeedback(''), 5000);
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div style={card}>
        <div style={sectionTitle}>Demand Tuning</div>
        <div style={{ color: 'var(--cn-muted)', fontSize: 13 }}>Loading…</div>
      </div>
    );
  }

  return (
    <>
      {preview && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{
            background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.2)',
            borderRadius: 8, padding: '28px 32px', maxWidth: 420, width: '90%',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--cn-text)', marginBottom: 16 }}>
              Queue Next Batch
            </div>
            <div style={{ fontSize: 13, color: 'var(--cn-muted)', lineHeight: 1.7 }}>
              <div>Will queue <span style={{ color: 'var(--cn-text)', fontWeight: 600 }}>{preview.woMin}–{preview.woMax} work orders</span>,
                generating {preview.partsMin.toLocaleString()}–{preview.partsMax.toLocaleString()} parts.</div>
              <div style={{ marginTop: 8 }}>Current queue: <span style={{ color: 'var(--cn-text)', fontWeight: 600 }}>{preview.pendingWos} open WOs</span></div>
              <div>Machines: <span style={{ color: 'var(--cn-text)', fontWeight: 600 }}>{preview.runningMachines} running</span>, {preview.idleMachines} idle</div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button style={btnStyle('ghost')} onClick={() => setPreview(null)}>Cancel</button>
              <button style={btnStyle('primary')} onClick={() => { setPreview(null); apply('queue_next'); }}>
                Queue batch
              </button>
            </div>
          </div>
        </div>
      )}
      {confirm && (
        <ConfirmModal
          message={confirm.message}
          onConfirm={() => apply(confirm.mode)}
          onCancel={() => setConfirm(null)}
        />
      )}
      <div style={card}>
        <div style={sectionTitle}>Demand Tuning</div>

        {/* Min/max pairs + horizon */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 16 }}>
          <div>
            <span style={labelStyle}>WOs Min</span>
            <input type="number" min={1} max={200} value={config.wosMin}
              onChange={e => num('wosMin', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <span style={labelStyle}>WOs Max</span>
            <input type="number" min={1} max={200} value={config.wosMax}
              onChange={e => num('wosMax', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <span style={labelStyle}>Qty Min</span>
            <input type="number" min={1} max={500} value={config.qtyMin}
              onChange={e => num('qtyMin', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <span style={labelStyle}>Qty Max</span>
            <input type="number" min={1} max={500} value={config.qtyMax}
              onChange={e => num('qtyMax', e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={{ maxWidth: 200, marginBottom: 20 }}>
          <span style={labelStyle}>Horizon (days)</span>
          <input type="number" min={1} max={30} value={config.horizonDays}
            onChange={e => num('horizonDays', e.target.value)} style={inputStyle} />
        </div>

        {/* Horizon capacity hint */}
        <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginBottom: 16, lineHeight: 1.5 }}>
          Horizon cap: {config.horizonDays} day{config.horizonDays !== 1 ? 's' : ''} ×
          960 parts/day = <span style={{ color: 'var(--cn-muted)' }}>{config.horizonDays * 960} parts</span>.
          {' '}Each batch draws {config.wosMin}–{config.wosMax} WOs at {config.qtyMin}–{config.qtyMax} parts each,
          stopping early if the horizon cap is reached.
        </div>

        {/* Guard inverted ranges before they save nonsense into the capacity math (Postel). */}
        {(config.wosMin > config.wosMax || config.qtyMin > config.qtyMax) && (
          <div style={{ fontSize: 12, color: 'var(--cn-danger)', marginBottom: 12 }}>
            {config.wosMin > config.wosMax ? 'WOs Min can’t exceed WOs Max.' : 'Qty Min can’t exceed Qty Max.'}
          </div>
        )}
        {feedback && <div style={{ fontSize: 12, color: 'var(--cn-success)', marginBottom: 12 }}>{feedback}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          {/* Save is the routine action → primary gold. Recalculate DELETES open work orders
              → danger styling, so the eye isn't drawn to the destructive button (Von Restorff). */}
          <button style={btnStyle('primary')} onClick={save} disabled={saving || config.wosMin > config.wosMax || config.qtyMin > config.qtyMax}>Save settings</button>
          <button
            style={btnStyle('danger')}
            disabled={saving || config.wosMin > config.wosMax || config.qtyMin > config.qtyMax}
            onClick={async () => {
              let pendingCount = '?';
              try {
                const r = await axios.get(`${BASE_API}/erp/work-orders${cq}`);
                const pending = (r.data.workOrders || []).filter(w => w.status !== 'COMPLETED');
                pendingCount = pending.length;
              } catch { /* use '?' on error */ }
              setConfirm({
                mode: 'recalculate',
                message: `Delete ${pendingCount} open work order${pendingCount !== 1 ? 's' : ''} and regenerate from current parameters? This cannot be undone.`,
              });
            }}
          >
            Recalculate Now
          </button>
          <button
            style={btnStyle('ghost')}
            onClick={async () => {
              setPreviewLoading(true);
              try {
                const [machRes, woRes] = await Promise.all([
                  axios.get(`${BASE_API}/machines${cq}`),
                  axios.get(`${BASE_API}/erp/work-orders${cq}`),
                ]);
                const machines = machRes.data.machines || [];
                const wos      = woRes.data.workOrders || [];
                const pending  = wos.filter(w => w.status !== 'COMPLETED').length;
                const running  = machines.filter(m => m.phase && m.phase !== 'IDLE').length;
                const idle     = machines.length - running;
                setPreview({
                  woMin:           config.wosMin,
                  woMax:           config.wosMax,
                  partsMin:        config.wosMin * config.qtyMin,
                  partsMax:        config.wosMax * config.qtyMax,
                  pendingWos:      pending,
                  runningMachines: running,
                  idleMachines:    idle,
                });
              } finally { setPreviewLoading(false); }
            }}
            disabled={saving || previewLoading}
          >
            {previewLoading ? 'Loading…' : 'Queue Next Batch'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Section 3: Machine Archetypes (read-only catalog) ───────────
function ArchetypesSection() {
  const [archetypes, setArchetypes] = useState([]);
  const [expanded, setExpanded]     = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    axios.get(`${BASE_API}/archetypes`)
      .then(r => setArchetypes(r.data.archetypes))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={card}>
      <div style={sectionTitle}>Machine Archetypes</div>
      {loading ? (
        <div style={{ color: 'var(--cn-muted)', fontSize: 13 }}>Loading…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr><TH></TH><TH>Name</TH><TH>Category</TH><TH>Cycle Time</TH><TH>Setup Time</TH><TH>Tools</TH></tr>
          </thead>
          <tbody>
            {archetypes.map(a => (
              <>
                <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => setExpanded(e => e === a.id ? null : a.id)}>
                  <td style={{ padding: '8px 10px', width: 24 }}>
                    <span style={{ color: 'var(--cn-muted)', fontSize: 11 }}>{expanded === a.id ? '▼' : '▶'}</span>
                  </td>
                  <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--cn-text)' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: a.colorHex, marginRight: 8, verticalAlign: 'middle' }} />
                    {a.name}
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--cn-muted)', textTransform: 'capitalize' }}>{a.category}</td>
                  <td style={{ padding: '8px 10px', fontFamily: '"Courier New", monospace', color: 'var(--cn-text)' }}>{a.cycleTimeMinSec}–{a.cycleTimeMaxSec}s</td>
                  <td style={{ padding: '8px 10px', fontFamily: '"Courier New", monospace', color: 'var(--cn-text)' }}>{a.setupTimeMinSec}–{a.setupTimeMaxSec}s</td>
                  <td style={{ padding: '8px 10px', color: 'var(--cn-muted)' }}>{a.tools.length}</td>
                </tr>
                {expanded === a.id && (
                  <tr key={a.id + '-exp'}>
                    <td colSpan={6} style={{ padding: '0 10px 12px 36px' }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {a.tools.map(t => (
                          <div key={t.id} style={{ background: 'rgba(168,144,96,0.06)', border: '1px solid rgba(168,144,96,0.15)', borderRadius: 4, padding: '6px 12px', fontSize: 12 }}>
                            <span style={{ fontFamily: '"Courier New", monospace', color: 'var(--cn-gold)', fontWeight: 700 }}>T:{t.toolKey}</span>
                            <span style={{ color: 'var(--cn-muted)', marginLeft: 8 }}>pool: [{t.presetPool}]</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Section 4: Sim Controls ──────────────────────────────────────
function SimControlsSection({ engineStatus, onRefresh, companySlug }) {
  const [confirm, setConfirm]   = useState(null);
  const [busy, setBusy]         = useState(false);
  const [feedback, setFeedback] = useState('');

  const machines = Object.values(engineStatus);
  const allPaused = machines.length > 0 && machines.every(m => m.isPaused);

  const cq = companySlug && companySlug !== 'default' ? `?company=${companySlug}` : '';

  const run = async (action) => {
    setConfirm(null);
    setBusy(true);
    try {
      await axios.post(`${SIM_API}/${action}${cq}`);
      setFeedback(
        action === 'pause-all'  ? 'All machines paused'  :
        action === 'resume-all' ? 'All machines resumed' :
        'All machines reset'
      );
      setTimeout(() => setFeedback(''), 3000);
      onRefresh();
    } finally { setBusy(false); }
  };

  return (
    <>
      {confirm && (
        <ConfirmModal
          message={confirm.message}
          onConfirm={() => run(confirm.action)}
          onCancel={() => setConfirm(null)}
        />
      )}
      <div style={card}>
        <div style={sectionTitle}>Sim Controls</div>
        {feedback && <div style={{ fontSize: 12, color: 'var(--cn-success)', marginBottom: 12 }}>{feedback}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            style={btnStyle(allPaused ? 'primary' : 'ghost')}
            onClick={() => run(allPaused ? 'resume-all' : 'pause-all')}
            disabled={busy}
          >
            {allPaused ? 'Resume All' : 'Pause All'}
          </button>
          <button
            style={btnStyle('danger')}
            disabled={busy}
            onClick={() => setConfirm({ action: 'reset-all', message: 'Reset all machines? This will clear active work orders from the engine.' })}
          >
            Reset All
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Section: Factory Reset ──────────────────────────────────────
function FactoryResetSection() {
  const [companies, setCompanies]   = useState([]);
  const [selected, setSelected]       = useState(new Set());
  const [confirm, setConfirm]         = useState(false);
  const [busy, setBusy]               = useState(false);
  const [feedback, setFeedback]       = useState('');

  const auth        = useMMAuth();
  const accessToken = auth?.accessToken ?? null;
  const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

  useEffect(() => {
    axios.get(`${BASE_API}/companies`, { headers: authHeaders })
      .then(r => setCompanies(r.data.companies || r.data || []))
      .catch(() => setCompanies([]));
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (slug) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    return next;
  });

  const runReset = async () => {
    setConfirm(false);
    setBusy(true);
    setFeedback('');
    try {
      const slugs = [...selected];
      const r = await axios.post(`${SIM_API}/factory-reset`, { companies: slugs });
      const summary = (r.data.results || [])
        .map(x => `${x.company}: ${x.deletedWoCount} WOs deleted, ${x.batchCreated} created`)
        .join('; ');
      setFeedback(summary || 'Factory reset complete');
      setTimeout(() => setFeedback(''), 8000);
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Factory reset failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      {confirm && (
        <ConfirmModal
          message={`Delete ALL work orders and reset every machine to idle for: ${[...selected].join(', ')}? Fresh batches will be generated from demand config.`}
          onConfirm={runReset}
          onCancel={() => setConfirm(false)}
        />
      )}
      <div style={card}>
        <div style={sectionTitle}>Factory Reset</div>
        <p style={{ fontSize: 13, color: 'var(--cn-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
          Clears all work orders, resets sim machine state to idle, reconciles engine, and regenerates the next WO batch per company demand config.
        </p>
        {feedback && (
          <div style={{ fontSize: 12, color: feedback.includes('failed') ? 'var(--cn-danger)' : 'var(--cn-success)', marginBottom: 12 }}>
            {feedback}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {companies.map(c => (
            <label key={c.slug || c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--cn-text)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selected.has(c.slug)}
                onChange={() => toggle(c.slug)}
                style={{ accentColor: 'var(--cn-gold)' }}
              />
              {c.name || c.slug}
              <span style={{ color: 'var(--cn-dimmed)', fontSize: 11 }}>({c.slug})</span>
            </label>
          ))}
          {!companies.length && <span style={{ fontSize: 12, color: 'var(--cn-muted)' }}>Loading companies…</span>}
        </div>
        <button
          style={btnStyle('danger')}
          disabled={busy || selected.size === 0}
          onClick={() => setConfirm(true)}
        >
          {busy ? 'Resetting…' : 'Factory Reset Selected'}
        </button>
      </div>
    </>
  );
}

// ─── Section 5: Machine Management ───────────────────────────────
function MachineManagementSection({ companySlug, isOwner }) {
  const [machines, setMachines]     = useState([]);
  const [archetypes, setArchetypes] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [form, setForm]             = useState({ name: '', machineType: 'LATHE', archetypeId: '' });
  const [saving, setSaving]         = useState(false);
  const [feedback, setFeedback]     = useState('');

  const TYPES = ['PRESS', 'MILL', 'LATHE', 'LASER', 'SWISS_LATHE', 'PRESS_BRAKE'];
  const cq = companySlug && companySlug !== 'default' ? `?company=${companySlug}` : '';

  const load = useCallback(async () => {
    const [mRes, aRes] = await Promise.all([
      axios.get(`${BASE_API}/machines/catalog${cq}`),
      axios.get(`${BASE_API}/archetypes`),
    ]);
    setMachines(mRes.data.machines);
    setArchetypes(aRes.data.archetypes);
    setLoading(false);
  }, [cq]);

  useEffect(() => { load(); }, [load]);

  const addMachine = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setFeedback('');
    try {
      const nameUpper = form.name.trim().toUpperCase();
      await axios.post(`${BASE_API}/machines/catalog${cq}`, {
        name: nameUpper,
        machineType: form.machineType,
        archetypeId: form.archetypeId || undefined,
      });
      setForm({ name: '', machineType: 'LATHE', archetypeId: '' });
      setShowAdd(false);
      setFeedback(`Machine "${nameUpper}" added`);
      setTimeout(() => setFeedback(''), 3000);
      await load();
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Failed to add machine');
    } finally { setSaving(false); }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={sectionTitle}>Machine Management</div>
        {isOwner !== false && (
          <button style={btnStyle('ghost')} onClick={() => setShowAdd(s => !s)}>
            {showAdd ? 'Cancel' : '+ Add Machine'}
          </button>
        )}
      </div>

      {feedback && (
        <div style={{ fontSize: 12, color: feedback.startsWith('Failed') || feedback.startsWith('Machine') && feedback.includes('already') ? 'var(--cn-danger)' : 'var(--cn-success)', marginBottom: 12 }}>
          {feedback}
        </div>
      )}

      {showAdd && (
        <div style={{ background: 'rgba(168,144,96,0.05)', border: '1px solid rgba(168,144,96,0.15)', borderRadius: 6, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'flex-end' }}>
            <div>
              <span style={labelStyle}>Machine Name</span>
              <input
                placeholder="e.g. LATHE-03"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <span style={labelStyle}>Machine Type</span>
              <select
                value={form.machineType}
                onChange={e => setForm(f => ({ ...f, machineType: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <span style={labelStyle}>Archetype</span>
              <select
                value={form.archetypeId}
                onChange={e => setForm(f => ({ ...f, archetypeId: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">— none —</option>
                {archetypes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <button style={btnStyle('primary')} onClick={addMachine} disabled={saving || !form.name.trim()}>
              {saving ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--cn-muted)', fontSize: 13 }}>Loading…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr><TH>Name</TH><TH>Type</TH><TH>Archetype</TH><TH>Phase</TH></tr>
          </thead>
          <tbody>
            {machines.map(m => (
              <tr key={m.id}>
                <td style={{ padding: '8px 10px', fontFamily: '"Courier New", monospace', fontWeight: 600, color: 'var(--cn-text)' }}>{m.displayName || machineDisplayName(m.name, companySlug)}</td>
                <td style={{ padding: '8px 10px', color: 'var(--cn-muted)' }}>{m.machineType}</td>
                <td style={{ padding: '8px 10px' }}>
                  {m.archetype ? (
                    <span>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: m.archetype.colorHex, marginRight: 6, verticalAlign: 'middle' }} />
                      <span style={{ color: 'var(--cn-text)' }}>{m.archetype.name}</span>
                    </span>
                  ) : (
                    <span style={{ color: 'var(--cn-muted)' }}>—</span>
                  )}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {m.engineState
                    ? <StatusBadge phase={m.engineState.phase} />
                    : <span style={{ color: 'var(--cn-muted)', fontSize: 11 }}>not in engine</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────
// ─── Section 6: Capacity Analysis ────────────────────────────────
function CapacitySection({ companySlug }) {
  const [demandConfig, setDemandConfig] = useState(null);
  const [parts, setParts]               = useState([]);
  const [machines, setMachines]         = useState([]);
  const [mode, setMode]                 = useState('random');
  const [weights, setWeights]           = useState({});
  const [saving, setSaving]             = useState(false);
  const [feedback, setFeedback]         = useState('');

  const cq = companySlug && companySlug !== 'default' ? `?company=${companySlug}` : '';

  useEffect(() => {
    Promise.all([
      axios.get(`${SIM_API}/demand${cq}`).then(r => r.data.demand),
      axios.get(`${BASE_API}/parts${cq}`).then(r => r.data.parts ?? []),
      axios.get(`${BASE_API}/machines${cq}`).then(r => r.data.machines ?? []),
    ]).then(([demand, partsData, machinesData]) => {
      setDemandConfig(demand);
      setMode(demand.partDistributionMode ?? 'random');
      setWeights(demand.partWeights ?? {});
      setParts(partsData);
      setMachines(machinesData);
    }).catch(() => {});
  }, [companySlug]);

  const analysis = demandConfig && parts.length && machines.length
    ? calculateCapacity({ ...demandConfig, partDistributionMode: mode, partWeights: weights }, parts, machines)
    : [];

  const saveDistribution = async () => {
    setSaving(true);
    try {
      await axios.post(`${SIM_API}/demand${cq}`, {
        partDistributionMode: mode,
        partWeights:          weights,
      });
      setFeedback('Saved');
      setTimeout(() => setFeedback(''), 2500);
    } finally { setSaving(false); }
  };

  const statusColor = (status) => {
    if (status === 'bottleneck') return '#f87171';
    if (status === 'surplus')    return 'var(--cn-warning)';
    return 'var(--cn-success)';
  };
  const barColor = (pct) => {
    if (pct > 90) return '#f87171';
    if (pct > 75) return 'var(--cn-warning)';
    return 'var(--cn-success)';
  };

  return (
    <div style={card}>
      <div style={sectionTitle}>Capacity Analysis</div>
      <div style={{ fontSize: 11, color: 'var(--cn-dimmed)', marginBottom: 16 }}>
        Based on current demand settings
      </div>

      {analysis.length === 0 && (
        <div style={{ color: 'var(--cn-muted)', fontSize: 13 }}>
          {demandConfig ? 'No machine types found for capacity calculation.' : 'Loading…'}
        </div>
      )}

      {analysis.map(row => (
        <div key={row.type} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cn-text)', minWidth: 120, fontFamily: '"Courier New", monospace' }}>
              {row.type.replace(/_/g, ' ')}
            </span>
            <div style={{ flex: 1, height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(row.utilizationPct, 100)}%`, background: barColor(row.utilizationPct), borderRadius: 4, transition: 'width 0.4s' }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: barColor(row.utilizationPct), minWidth: 40, textAlign: 'right' }}>
              {row.utilizationPct}%
            </span>
            <span style={{ fontSize: 16 }}>
              {row.status === 'bottleneck' ? '⚠' : row.status === 'surplus' ? '↓' : '✓'}
            </span>
          </div>
          {row.suggestion && (
            <div style={{ fontSize: 11, color: statusColor(row.status), marginLeft: 132 }}>
              {row.suggestion}
            </div>
          )}
        </div>
      ))}

      {/* Part distribution sub-section */}
      {parts.length > 0 && (
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(168,144,96,0.1)' }}>
          <div style={{ ...sectionTitle, fontSize: 10, marginBottom: 12 }}>Part Distribution</div>
          <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
            {[['random', 'Random (equal weight)'], ['weighted', 'Custom weights']].map(([val, label]) => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: mode === val ? 'var(--cn-text)' : 'var(--cn-muted)' }}>
                <input
                  type="radio"
                  name="distMode"
                  value={val}
                  checked={mode === val}
                  onChange={() => setMode(val)}
                  style={{ accentColor: 'var(--cn-gold)' }}
                />
                {label}
              </label>
            ))}
          </div>

          {mode === 'weighted' && parts.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--cn-muted)', minWidth: 180 }}>{p.name}</span>
              <input
                type="range"
                min={0}
                max={100}
                value={weights[p.id] ?? 50}
                onChange={e => setWeights(w => ({ ...w, [p.id]: Number(e.target.value) }))}
                style={{ width: 140, accentColor: 'var(--cn-gold)' }}
              />
              <span style={{ fontSize: 12, color: 'var(--cn-gold)', minWidth: 28, textAlign: 'right' }}>
                {weights[p.id] ?? 50}
              </span>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
            <button style={btnStyle('primary')} onClick={saveDistribution} disabled={saving}>
              {saving ? 'Saving…' : 'Save distribution'}
            </button>
            {feedback && <span style={{ fontSize: 12, color: 'var(--cn-success)' }}>{feedback}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section: Tool-Life Inefficiency ─────────────────────────────
// Per-machine-type "dud" model. `frequency` = chance a fresh tool dies early; a dud tool is pulled at
// somewhere in [min, max] of its rated preset. The engine caches this at hydrate, so a save takes
// effect only after the server restarts (flagged below). Rows: a company-wide default + LATHE + MILL.
const TI_ROWS = [
  { key: 'default', label: 'Company default' },
  { key: 'LATHE',   label: 'Lathes'  },
  { key: 'MILL',    label: 'Mills'   },
];
const TI_FALLBACK = {
  default: { frequency: 0.20, severityMin: 0.50, severityMax: 0.85 },
  LATHE:   { frequency: 0.25, severityMin: 0.50, severityMax: 0.85 },
  MILL:    { frequency: 0.15, severityMin: 0.50, severityMax: 0.85 },
};
const pct = (frac) => Math.round((Number(frac) || 0) * 100);

function ToolInefficiencySection({ companySlug, company, authHeaders, isOwner }) {
  const seed = () => {
    const src = company?.toolInefficiency || {};
    const out = {};
    for (const { key } of TI_ROWS) {
      const v = src[key] || TI_FALLBACK[key];
      out[key] = { frequency: pct(v.frequency), severityMin: pct(v.severityMin), severityMax: pct(v.severityMax) };
    }
    return out;
  };
  const [rows, setRows]       = useState(seed);
  const [saving, setSaving]   = useState(false);
  const [feedback, setFeedback] = useState('');

  // Re-seed once the company (with its saved config) actually arrives.
  useEffect(() => { setRows(seed()); }, [company?.slug, company?.toolInefficiency]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (rowKey, field, raw) => {
    const n = Math.max(0, Math.min(100, Math.round(Number(raw) || 0)));
    setRows(prev => ({ ...prev, [rowKey]: { ...prev[rowKey], [field]: n } }));
  };

  const save = async () => {
    if (companySlug === 'default') { setFeedback('Pick a company (not the default engine) to configure this.'); return; }
    setSaving(true); setFeedback('');
    const payload = {};
    for (const { key } of TI_ROWS) {
      const r = rows[key];
      const severityMin = r.severityMin / 100;
      payload[key] = {
        frequency:   r.frequency / 100,
        severityMin,
        severityMax: Math.max(severityMin, r.severityMax / 100),
      };
    }
    try {
      await axios.patch(`${BASE_API}/companies/${companySlug}`, { toolInefficiency: payload }, { headers: authHeaders });
      setFeedback('Saved — restart the engine (server) for this to take effect.');
      setTimeout(() => setFeedback(''), 6000);
    } catch (err) {
      setFeedback(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const readOnly = isOwner === false;
  const numCell = (rowKey, field, suffix) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input
        type="number" min={0} max={100} disabled={readOnly}
        value={rows[rowKey][field]}
        onChange={e => setField(rowKey, field, e.target.value)}
        style={{ ...inputStyle, width: 72, padding: '6px 8px', opacity: readOnly ? 0.6 : 1 }}
      />
      <span style={{ fontSize: 12, color: 'var(--cn-muted)' }}>{suffix}</span>
    </div>
  );

  return (
    <div style={card}>
      <div style={sectionTitle}>Tool-Life Inefficiency</div>
      <div style={{ fontSize: 12, color: 'var(--cn-muted)', marginBottom: 16, lineHeight: 1.5 }}>
        A fraction of fresh tools wear out early and get changed before reaching their rated life.
        <b> Frequency</b> is how often that happens; <b>early life</b> is the window (as a % of the
        tool’s preset) a worn tool is pulled within. The displayed preset on the wire is unchanged.
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr><TH>Scope</TH><TH>Frequency</TH><TH>Early life min</TH><TH>Early life max</TH></tr>
        </thead>
        <tbody>
          {TI_ROWS.map(({ key, label }) => (
            <tr key={key}>
              <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--cn-text)' }}>{label}</td>
              <td style={{ padding: '8px 10px' }}>{numCell(key, 'frequency', '% of tools')}</td>
              <td style={{ padding: '8px 10px' }}>{numCell(key, 'severityMin', '% of preset')}</td>
              <td style={{ padding: '8px 10px' }}>{numCell(key, 'severityMax', '% of preset')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16 }}>
          <button style={btnStyle('primary')} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save inefficiency config'}
          </button>
          {feedback && (
            <span style={{ fontSize: 12, color: feedback.startsWith('Failed') || feedback.startsWith('Pick') ? 'var(--cn-danger)' : 'var(--cn-success)' }}>
              {feedback}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const companySlug = searchParams.get('company') || DEFAULT_COMPANY;

  const [engineStatus, setEngineStatus] = useState({});
  const [company, setCompany]           = useState(null);

  const { user: currentUser } = useCurrentUser();
  const auth        = useMMAuth();
  const accessToken = auth?.accessToken ?? null;
  const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

  const cq = companySlug !== 'default' ? `?company=${companySlug}` : '';

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${SIM_API}/status${cq}`);
      setEngineStatus(r.data || {});
    } catch {}
  }, [companySlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 3000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  useEffect(() => {
    if (companySlug === 'default') { setCompany(null); return; }
    axios.get(`${BASE_API}/companies/${companySlug}`, { headers: authHeaders })
      .then(r => setCompany(r.data.company || r.data))
      .catch(() => setCompany(null));
  }, [companySlug, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOwner = !company || (currentUser && company.ownerEmail === currentUser.email);
  const showPublicBanner = company?.isPublic && !isOwner;

  return (
    <div style={{ padding: '32px 36px', maxWidth: 1100 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--cn-gold)', marginBottom: 4 }}>
        Configuration
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--cn-text)', margin: '0 0 28px' }}>
        Settings
      </h1>

      {showPublicBanner && (
        <div style={{
          fontSize: 13, color: 'var(--cn-muted)', marginBottom: 20,
          padding: '10px 16px', background: 'rgba(46,125,94,0.08)',
          borderRadius: 6, border: '1px solid rgba(46,125,94,0.2)',
        }}>
          This is a public company. Settings are read-only for non-owners.
        </div>
      )}

      <SpeedSection engineStatus={engineStatus} companySlug={companySlug} />
      <DemandSection companySlug={companySlug} />
      <CapacitySection companySlug={companySlug} />
      <ArchetypesSection />
      <ToolInefficiencySection companySlug={companySlug} company={company} authHeaders={authHeaders} isOwner={isOwner} />
      <SimControlsSection engineStatus={engineStatus} onRefresh={fetchStatus} companySlug={companySlug} />
      <FactoryResetSection />
      <MachineManagementSection companySlug={companySlug} isOwner={isOwner} />
    </div>
  );
}
